import JSCookie from 'js-cookie';
import type { NextComponentType } from 'next';
import type { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React from 'react';

import { ThemeName, ThemeNames } from '@sravni/design-system-theme';
import type { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { CommonHead } from '@sravni/next-common-head';
import { Notifications } from '@sravni/react-design-system';
import { Footer } from '@sravni/react-footer';
import { AppAnalyticsPixel } from '@sravni/react-ugc/lib/AppAnalyticsPixel';
import { ConfigProvider, DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';
import { sentryInit } from '@sravni/ugc/lib/utils/sentry/sentryInit';
import { formatUserData } from '@sravni/utils/lib/auth';

import { Header } from '../components/Header';
import { Seo } from '../components/Seo';
import { TopBanner } from '../components/TopBanner';
import { DEFAULT_FINGER_PRINT_VALUE } from '../constants/fingerPrint';
import { PRODUCT_TYPE } from '../constants/reviews';
import { loadUseDesk } from '../helpers/loadUseDesk';
import { getReviewType } from '../helpers/reviews';
import { useIsWlPage } from '../hooks/useIsWlPage';
import { StoreProvider } from '../providers/store';
import { loadUserInfo } from '../services/reviews';
import type { GlobalState } from '../store';
import { getOrCreateStore } from '../store';
import { setAllReviewsFilter } from '../store/reducers/filters';
import { setCurrentLocation, setLocations } from '../store/reducers/locations';
import { setCurrentReviewType } from '../store/reducers/reviews';
import { setSettings } from '../store/reducers/sitesettings';
import { setFingerprint, setUser, setUserInfo } from '../store/reducers/user';
import { selectProductsTags } from '../store/selectors/filters';
import { selectOrganizationsList } from '../store/selectors/organizations';
import { getFingerprint } from '../store/selectors/users';
import styles from '../styles/page.module.scss';
import { isProdBuild } from '../utils/environment';

import '@sravni/design-system-theme/lib/globalStyles.css';
import '@sravni/ugc/lib/theme/articleTypography.scss';
import 'intersection-observer';
import '../styles/global.scss';
import '../components/Wysiwyg/quill.sravni.scss';

const runtimeConfig = {
  gatewayUrl: getConfig().publicRuntimeConfig.gatewayUrl,
  publicUrl: getConfig().publicRuntimeConfig.publicUrl,
  environment: getConfig().publicRuntimeConfig.environment,
};

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
}

export const AB_NAME = 'AB_REVIEWS';

const { webPath } = getConfig().publicRuntimeConfig;

const MyApp: NextComponentType<App.ReduxAppContext, AppInitialProps, IProps> = (props) => {
  const {
    Component,
    // @ts-ignore
    Component: { iframe },
    pageProps,
    initialReduxState,
    deviceInfo,
    theme,
  } = props;
  const { asPath } = useRouter();
  const reduxStore = getOrCreateStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const { user, sitesettings } = getState();
  const { publicRuntimeConfig } = getConfig();
  const organizationsList = selectOrganizationsList(getState());
  const subId = user.loggedIn ? user.account?.sub : undefined;
  const isWl = useIsWlPage();

  const getUserInfo = async () => {
    const userInfo = await loadUserInfo();
    dispatch(setUserInfo(userInfo));
    loadUseDesk(userInfo, organizationsList);
  };

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { queue } = require('../utils/queue');
    const fp = getFingerprint(getState());

    if (fp === DEFAULT_FINGER_PRINT_VALUE) {
      import('@fingerprintjs/fingerprintjs')
        .then((FingerprintJS) => FingerprintJS.load())
        .then((fp) => fp.get())
        .then((result) => result.visitorId)
        .then((newId) => {
          dispatch(setFingerprint(newId));
          JSCookie.set('_FP_', newId);
        });
    }

    if (isProdBuild) {
      queue.push(() => {
        sentryInit({
          sentryDSN: publicRuntimeConfig.sentryDSN,
          environment: publicRuntimeConfig.environment,
          release: publicRuntimeConfig.release,
          user: user?.account,
          // ignoreErrors: [],
        });
      });

      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message);
          });
        }
      });
    }

    if (user.loggedIn && !iframe) getUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pathsWithIOSApp = new RegExp('/otzyvy?');
  const routeForIOSSmartBanner = pathsWithIOSApp.test(asPath);

  const content = (
    <>
      <CommonHead
        analyticsOptions={{
          enabled: isProdBuild,
          googleTmId: 'GTM-PMDFG9',
          ab: AB_NAME,
          userId: subId,
          theme,
          yandexMetricsOptions: {},
        }}
      >
        {routeForIOSSmartBanner && (
          <meta name="apple-itunes-app" content={`app-id=1449578663, app-argument=${webPath}${asPath}`} />
        )}
      </CommonHead>
      {routeForIOSSmartBanner && <AppAnalyticsPixel />}
      <Seo />
      {!isWl && (
        <>
          <TopBanner />
          <Header />
        </>
      )}
      <div className={styles.wrapper}>
        <Component {...pageProps} />
      </div>
      {!isWl && (
        <div className={styles.footerWrapper}>
          <Footer menuLinks={sitesettings.footer?.menu} socialLinks={sitesettings.footer?.socialLinks} />
        </div>
      )}
      <Notifications />
    </>
  );

  return (
    <StoreProvider store={reduxStore}>
      <DeviceInfoProvider initialInfo={deviceInfo}>
        <ThemeProvider initialTheme={theme}>
          <ConfigProvider value={runtimeConfig}>{content}</ConfigProvider>
        </ThemeProvider>
      </DeviceInfoProvider>
    </StoreProvider>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext<{ type: PAGE_TYPE | PRODUCT_TYPE }>) => {
  let pageProps = {};
  const reduxStore = getOrCreateStore();
  const { dispatch, getState } = reduxStore;

  ctx.store = reduxStore;

  const {
    __USER__,
    __SITE_SETTINGS__,
    __SELECTED_LOCATION__,
    __REVIEWS_FILTERS__,
    __REGIONAL_CENTERS__,
    __REGIONS__,
    __DEVICE_INFO__,
    __THEME__,
  } = ctx.req ?? {};

  if (typeof window === 'undefined') {
    if (__USER__) {
      dispatch(setUser(formatUserData(__USER__)));
    }
    if (__SITE_SETTINGS__) {
      dispatch(setSettings(__SITE_SETTINGS__));
    }
    if (__SELECTED_LOCATION__) {
      dispatch(setCurrentLocation(__SELECTED_LOCATION__));
    }
    if (__REVIEWS_FILTERS__) {
      dispatch(setAllReviewsFilter(__REVIEWS_FILTERS__));
    }
    if (__REGIONAL_CENTERS__ && __REGIONS__) {
      dispatch(
        setLocations({
          centers: __REGIONAL_CENTERS__,
          regions: __REGIONS__,
        }),
      );
    }

    const products = selectProductsTags(getState());

    if (ctx.query.type) {
      const typePage = getReviewType(ctx.query.type, products);

      dispatch(setCurrentReviewType(typePage));
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  let deviceInfo: DeviceInfo = { phone: false, tablet: false, isAppleMobile: false };
  let theme: ThemeNames = ThemeName.lager;

  if (__DEVICE_INFO__) {
    deviceInfo = __DEVICE_INFO__;
  }
  if (__THEME__) {
    theme = __THEME__;
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    deviceInfo,
    realPath: ctx.req?.url || '',
    theme,
  };
};

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }

export default MyApp;
